import { getUserInfo } from "redux/selectors";
import { useSelector } from "react-redux";
import { useUpdate } from "hooks/useUpdate";
import { Checkbox, notification, Spin } from "antd";
import queryKeys from "common/queryKey";
import moment from "moment";
import Button from "atoms/Button";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useQueryState } from "hooks/useQueryState";
import { useQueryClient } from "react-query";
import { useState } from "react";
import DeclineTimesheetModal from "./DeclineTimesheetModal";
import { CALL_API } from "common/API";

const ApproveActionButton = ({
  selectedTimesheetKeys,
  setSelectedTimesheetKeys,
}) => {
  const userInfo = useSelector((state) => getUserInfo(state));
  const queryClient = useQueryClient();

  const { updateTimesheet, getTimesheet } = queryKeys;

  const { week, status } = useParams();

  const [loading, setLoading] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const { data: timesheetDataRes } = useQueryState(
    `${queryKeys.getTimesheet.key}-week:${week}`
  );

  const timesheetData = timesheetDataRes?.response?.response || {};

  const getActualSummary = async (engagementId, userId) => {
    try {
      const { data } = await CALL_API(
        `/insyts/timesheet/actualsummary?engagementId=${engagementId}&userId=${userId}`,
        "get"
      );

      return data;
    } catch (error) {
      console.error("Failed to fetch actualToDate:", error);
      return null;
    }
  };

  const approveTimesheet = () => {
    setLoading(true);
    const updatedTimeSheet = [];
    const newTimeSheet = [...timesheetData.timeSheet];

    for (let i = 0; i < selectedTimesheetKeys?.length; i++) {
      const idx = selectedTimesheetKeys[i];

      if (newTimeSheet[idx]) {
        newTimeSheet[idx] = {
          ...newTimeSheet[idx],
          status: "approved",
          approval: {
            by: userInfo.employer_id,
            date: moment().format("DD-MM-YYYY"),
          },
        };
        updatedTimeSheet.push(newTimeSheet[idx]);
      }
    }

    approveUpdate(
      {
        ...timesheetData,
        updatedTimeSheet,
        timeSheet: newTimeSheet,
      },
      {
        onSuccess: async () => {
          queryClient.invalidateQueries(`${getTimesheet.key}-week:${week}`);
          const updatedTimeSheet = await Promise.all(
            (timesheetData?.timeSheet || []).map(async (item) => {
              const actualSummary = await getActualSummary(
                item?.engagement,
                timesheetData?.employeeId
              );

              if (item.personInCharge === userInfo?.userid) {
                const hasExisting =
                  (actualSummary?.actualTimesheet || []).length > 0;

                const actualTimesheet = hasExisting
                  ? actualSummary.actualTimesheet.map((summaryItem) => ({
                      ...summaryItem,
                      status:
                        summaryItem?.week === timesheetData?.week
                          ? "approved"
                          : summaryItem?.status,
                    }))
                  : [
                      {
                        resourceId: item.resourceId ?? null,
                        week: timesheetData?.week,
                        sum: item.sum || 0,
                        actualhours: item.actual_to_date ?? 0,
                        status: "approved",
                      },
                    ];

                return {
                  engagementId: item?.engagement,
                  userId: timesheetData?.employeeId,
                  actualTimesheet,
                  _method: hasExisting ? "patch" : "post",
                };
              }

              return item;
            })
          );
          for (const entry of updatedTimeSheet) {
            if (entry?.actualTimesheet) {
              const { _method, ...actualSummaryPayload } = entry;
              const { code } = await CALL_API(
                `insyts/timesheet/actualsummary`,
                _method || "patch",
                actualSummaryPayload
              );
            }
          }
          setLoading(false);
          notification.success({
            message: "Timesheets Approved Successfully",
          });
          setSelectedTimesheetKeys([]);
        },
      }
    );
  };

  const { mutate: approveUpdate } = useUpdate(
    updateTimesheet.key,
    updateTimesheet.url
  );

  const handleSelectAll = (checked) => {
    if (checked) {
      const allKeys = timesheetData?.timeSheet
        ?.map((item, index) =>
          item.status === "pending" && item?.personInCharge === userInfo?.userid
            ? index
            : -1
        )
        ?.filter((index) => index !== -1);
      setSelectedTimesheetKeys(allKeys);
    } else {
      setSelectedTimesheetKeys([]);
    }
  };

  return (
    <>
      {status === "pending" && (
        <>
          <div className="d-flex align-items-center gap-2 mb-3 mt-3">
            <Button
              style={{ marginLeft: 0 }}
              disabled={!selectedTimesheetKeys?.length > 0}
              onClick={approveTimesheet}
              type="primary"
              loading={loading}
            >
              {" "}
              Approve{" "}
            </Button>
            <Button
              disabled={!selectedTimesheetKeys?.length > 0}
              onClick={() => {
                setShowDeclineModal(true);
              }}
              title="Decline Timesheet"
              type="primary"
            >
              {" "}
              Decline{" "}
            </Button>
            <DeclineTimesheetModal
              open={showDeclineModal}
              setOpen={setShowDeclineModal}
              selectedTimesheetKeys={selectedTimesheetKeys}
              setSelectedTimesheetKeys={setSelectedTimesheetKeys}
            />
          </div>

          <Checkbox
            className="mb-3"
            onChange={(e) => handleSelectAll(e.target.checked)}
            checked={
              selectedTimesheetKeys?.length > 0 ||
              !timesheetData?.timeSheet?.some(
                (item) =>
                  item?.status === "pending" &&
                  item?.personInCharge === userInfo?.userid
              )
            }
            disabled={
              !timesheetData?.timeSheet?.some(
                (item) => item?.status === "pending"
              )
            }
          >
            <span style={{ color: "black" }}>Select All</span>
          </Checkbox>
        </>
      )}
    </>
  );
};

export default ApproveActionButton;
